// Deterministic models of password storage and login for Lesson 2.8 ("Prove Who Is
// Asking"). They show who can read a stored password, how long a guess list lasts,
// what a salt changes and what a failed login reveals. Nothing is hashed at run time:
// every hash below is a real value computed once offline and hard-coded, with the
// command that produced it.

export type AuthUserKey = 'maya' | 'ravi' | 'jo';
export interface AuthUser {
  id: number;
  name: string;
  username: AuthUserKey;
  password: string;
}
/** The shared lesson dataset: Ravi and Jo chose the same common password. */
export const authUsers: readonly AuthUser[] = [
  { id: 1, name: 'Maya', username: 'maya', password: 'blue-kettle-42' },
  { id: 2, name: 'Ravi', username: 'ravi', password: 'summer2026' },
  { id: 3, name: 'Jo', username: 'jo', password: 'summer2026' },
];

/**
 * Unsalted SHA-256 of each lesson password, computed with Python 3.11:
 *   hashlib.sha256(b"blue-kettle-42").hexdigest()
 *   hashlib.sha256(b"summer2026").hexdigest()
 */
export const SHA256_OF: Readonly<Record<string, string>> = {
  'blue-kettle-42': '71e351c74e6628e0aa0fc4d41a8adf909c9aca00fdde96ce6772d729e057f66d',
  summer2026: '6a0436eecdad379345d804a9e4861a46e2135054879760acaf8d88534f871b26',
};

/**
 * Fixed example salts (16 bytes each, written as hex) so the graphics are repeatable.
 * Real code draws a new salt for every user with secrets.token_bytes(16).
 */
export const EXAMPLE_SALTS: Readonly<Record<AuthUserKey, string>> = {
  maya: '3f9a1c7e5b2d4086a1e3c5f7092b4d6e',
  ravi: '8c41d2e9f07a3b56c8e1d4a7b2f90c35',
  jo: 'e27b05f4a9c3168d5e0f7a2c94b6d813',
};

/**
 * Salted SHA-256 for the salt graphic, computed with Python 3.11:
 *   hashlib.sha256(bytes.fromhex(EXAMPLE_SALTS[user]) + password.encode()).hexdigest()
 */
export const SALTED_SHA256: Readonly<Record<AuthUserKey, string>> = {
  maya: '119bb5225a5e70a4cb8200d615f4cfe8718ddb16ac10458b500a49672ed5b201',
  ravi: 'd03a5ca22b4e95176a661dd3f5505c5602e28c8527e1d9303516543ad08252a7',
  jo: '1815f176cca9aaffd6da3dd64dc4eb98dabf6d4a34657ca52b04e93359e527d3',
};

/**
 * The stored scrypt strings, computed with Python 3.11:
 *   salt = bytes.fromhex(EXAMPLE_SALTS[user])
 *   digest = hashlib.scrypt(password.encode(), salt=salt, n=2**14, r=8, p=1, dklen=32)
 *   f"scrypt$16384$8$1${salt.hex()}${digest.hex()}"
 */
export const SCRYPT_STRINGS: Readonly<Record<AuthUserKey, string>> = {
  maya: 'scrypt$16384$8$1$3f9a1c7e5b2d4086a1e3c5f7092b4d6e$eda8e85673e24a9ca370edb01174517e1a68e3236eedd0054919e7aee4f44d54',
  ravi: 'scrypt$16384$8$1$8c41d2e9f07a3b56c8e1d4a7b2f90c35$db8e3a637c523ac0d7f547fa15a18c3967f3a6b2ac0970891f08edabd57b7cc0',
  jo: 'scrypt$16384$8$1$e27b05f4a9c3168d5e0f7a2c94b6d813$c68d5945e6fb1cc894b86d368c9d5eff6317cee2ddfc453d1ec7099b9483e433',
};

/**
 * Six entries of a precomputed table of common passwords, computed with Python 3.11:
 *   hashlib.sha256(word.encode()).hexdigest()
 */
export const PRECOMPUTED_TABLE: readonly { password: string; hash: string }[] = [
  { password: '123456', hash: '8d969eef6ecad3c29a3a629280e686cf0c3f5d5a86aff3ca12020c923adc6c92' },
  {
    password: 'password',
    hash: '5e884898da28047151d0e56f8dc6292773603d0d6aabbdd62a11ef721d1542d8',
  },
  { password: 'qwerty', hash: '65e84be33532fb784c48129675f9eff3a682b27168c0ea744b2cf58ee02337c5' },
  {
    password: 'iloveyou',
    hash: 'e4ad93ca07acb8d908a3aa41e920ea4f4ef4f26e7f86cf8291c5db289780a5ae',
  },
  { password: 'letmein', hash: '1c8bfe8f801d79745c4631d09fff36c82aa37fc4cce4fc946683d7b336b63032' },
  {
    password: 'summer2026',
    hash: '6a0436eecdad379345d804a9e4861a46e2135054879760acaf8d88534f871b26',
  },
];

/** The first `length` hex characters followed by an ellipsis. */
export const shortHex = (hex: string, length = 16) =>
  hex.length > length ? `${hex.slice(0, length)}…` : hex;

/** scrypt$16384$8$1$<salt>$<hash> with both hex parts shortened to 8 characters. */
export function shortScrypt(value: string) {
  const parts = value.split('$');
  if (parts.length !== 6) return value;
  const [name, n, r, p, salt = '', hash = ''] = parts;
  return `${name}$${n}$${r}$${p}$${shortHex(salt, 8)}$${shortHex(hash, 8)}`;
}

const listNames = (names: string[]) =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names.at(-1) ?? ''}`;

// --- auth-storage: every copy of the table is a copy of the passwords -------------------

export type StorageMode = 'plain' | 'scrypt';
export type StoragePlace = 'table' | 'backup' | 'log' | 'query';
export const storagePlaces: readonly { id: StoragePlace; label: string }[] = [
  { id: 'table', label: 'Users table' },
  { id: 'backup', label: "Last night's backup" },
  { id: 'log', label: 'Error log line' },
  { id: 'query', label: "Teammate's query" },
];
const placeWhere: Record<StoragePlace, string> = {
  table: 'PostgreSQL, read by the API and by anyone with database access.',
  backup: 'A dump file copied to backup storage every night.',
  log: 'A line the API wrote to its log while reporting an error.',
  query: "Output saved on a teammate's laptop after a debugging query.",
};

/** What the password column holds for one user; scrypt strings are shortened for display. */
export const storedValue = (mode: StorageMode, user: AuthUser) =>
  mode === 'plain' ? user.password : shortScrypt(SCRYPT_STRINGS[user.username]);
export const storageColumn = (mode: StorageMode) =>
  mode === 'plain' ? 'password' : 'password_hash';

/** psql's aligned output: centred headings, numbers on the right, text on the left. */
function psqlTable(columns: string[], rows: (string | number)[][]) {
  const widths = columns.map((column, index) =>
    Math.max(column.length, ...rows.map((row) => String(row[index] ?? '').length)),
  );
  const heading = columns
    .map((column, index) => {
      const spare = (widths[index] ?? 0) - column.length;
      const left = Math.floor(spare / 2);
      return ` ${' '.repeat(left)}${column}${' '.repeat(spare - left)} `;
    })
    .join('|')
    .trimEnd();
  const rule = widths.map((width) => '-'.repeat(width + 2)).join('+');
  const lines = rows.map((row) =>
    row
      .map((cell, index) => {
        const width = widths[index] ?? 0;
        return ` ${typeof cell === 'number' ? String(cell).padStart(width) : cell.padEnd(width)} `;
      })
      .join('|')
      .trimEnd(),
  );
  return [heading, rule, ...lines, `(${rows.length} rows)`].join('\n');
}

export interface StorageCopy {
  place: StoragePlace;
  label: string;
  where: string;
  column: string;
  /** The users table as rows: id, username, stored value. */
  rows: [number, string, string][];
  /** The copy as its reader sees it. */
  text: string;
  /** Display names whose password appears in this copy as written. */
  readable: string[];
}
export function storageCopy(mode: StorageMode, place: StoragePlace): StorageCopy {
  const column = storageColumn(mode);
  const rows = authUsers.map((user): [number, string, string] => [
    user.id,
    user.username,
    storedValue(mode, user),
  ]);
  const text =
    place === 'table'
      ? psqlTable(['id', 'username', column], rows)
      : place === 'backup'
        ? [
            '-- users.sql, written by pg_dump last night',
            `COPY public.users (id, name, username, ${column}) FROM stdin;`,
            ...authUsers.map(
              (user) => `${user.id}\t${user.name}\t${user.username}\t${storedValue(mode, user)}`,
            ),
            '\\.',
          ].join('\n')
        : place === 'log'
          ? `ERROR:api:weekly report failed; rows=[${authUsers
              .map((user) => `(${user.id}, '${user.username}', '${storedValue(mode, user)}')`)
              .join(', ')}]`
          : [
              `SELECT id, username, ${column} FROM users ORDER BY id;`,
              psqlTable(['id', 'username', column], rows),
            ].join('\n');
  return {
    place,
    label: storagePlaces.find((entry) => entry.id === place)?.label ?? place,
    where: placeWhere[place],
    column,
    rows,
    text,
    readable: authUsers.filter((user) => text.includes(user.password)).map((user) => user.name),
  };
}

export function storageSummary(mode: StorageMode): {
  passwords: number;
  places: number;
  message: string;
} {
  const copies = storagePlaces.map((place) => storageCopy(mode, place.id));
  const readable = new Set(copies.flatMap((copy) => copy.readable));
  const places = copies.filter((copy) => copy.readable.length).length;
  const message = readable.size
    ? `${readable.size} passwords readable in ${places} places. Anyone who opens any one of these copies can log in as ${listNames([...readable])}.`
    : `${readable.size} passwords readable. Every copy holds only scrypt strings: the server can still check a login attempt, but no copy gives a password back.`;
  return { passwords: readable.size, places, message };
}

// --- auth-guessing: a guess list against one leaked hash ------------------------------

export type GuessHash = 'sha256' | 'scrypt';
export type GuessList = '10k' | '1m' | '1b';
/** SHA-256 measured in the course workspace; one scrypt check (n=2**14) takes about 0.1 s. */
export const GUESS_RATES: Readonly<Record<GuessHash, number>> = { sha256: 1_500_000, scrypt: 10 };
export const GUESS_RATE_LABELS: Readonly<Record<GuessHash, string>> = {
  sha256: '1.5 million (measured in the course workspace)',
  scrypt: '10 (one scrypt check takes about 0.1 s)',
};
export const GUESS_LISTS: Readonly<Record<GuessList, { size: number; label: string }>> = {
  '10k': { size: 10_000, label: '10 thousand' },
  '1m': { size: 1_000_000, label: '1 million' },
  '1b': { size: 1_000_000_000, label: '1 billion' },
};
export const hashLabels: Readonly<Record<GuessHash, string>> = {
  sha256: 'SHA-256',
  scrypt: 'scrypt',
};

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;
/** A duration in the plainest unit: 0.007 s, 11 minutes, 28 hours, 3 years. */
export function formatDuration(seconds: number): string {
  if (seconds < 1) return `${Number(seconds.toPrecision(1))} s`;
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 3_600) return plural(Math.round(seconds / 60), 'minute');
  if (seconds < 3 * 86_400) return plural(Math.round(seconds / 3_600), 'hour');
  if (seconds < 365 * 86_400) return plural(Math.round(seconds / 86_400), 'day');
  return plural(Math.round(seconds / (365.25 * 86_400)), 'year');
}

/** summer2026 is among the 10 thousand most common passwords, so every list starts with it. */
export const RAVI_LIST: GuessList = '10k';

export function guessingResult(hash: GuessHash, list: GuessList) {
  const rate = GUESS_RATES[hash];
  const { size, label } = GUESS_LISTS[list];
  const seconds = size / rate;
  const time = formatDuration(seconds);
  const raviTime = formatDuration(GUESS_LISTS[RAVI_LIST].size / rate);
  const ravi =
    hash === 'sha256'
      ? list === RAVI_LIST
        ? "Ravi's summer2026 is on this list, so SHA-256 finds it at once."
        : `Ravi's summer2026 is among the first 10 thousand, so it falls within ${raviTime}.`
      : `Ravi's summer2026 is among the first 10 thousand, so scrypt still finds it within ${raviTime}: a slow hash makes guessing expensive, not impossible.`;
  return {
    rate,
    size,
    seconds,
    time,
    raviTime,
    raviFact: `On the 10 thousand list; found within ${raviTime}`,
    message: `${time} to try all ${label} guesses against one account. ${ravi}`,
  };
}

// --- auth-salt: equal passwords and a precomputed table -------------------------------

export interface SaltState {
  salted: boolean;
  looked: boolean;
}
export const initialSalt = (): SaltState => ({ salted: false, looked: false });
export type SaltAction =
  { kind: 'set-salt'; salted: boolean } | { kind: 'lookup' } | { kind: 'reset' };
export function saltTransition(state: SaltState, action: SaltAction): SaltState {
  switch (action.kind) {
    case 'reset':
      return initialSalt();
    case 'set-salt':
      return { salted: action.salted, looked: false };
    case 'lookup':
      return { ...state, looked: true };
  }
}
export interface SaltRow {
  name: string;
  salt: string | null;
  stored: string;
  /** The password the lookup found, or null when it found nothing. */
  found: string | null;
}
export function saltRows(state: SaltState): SaltRow[] {
  return authUsers.map((user) => {
    const stored = state.salted ? SALTED_SHA256[user.username] : (SHA256_OF[user.password] ?? '');
    return {
      name: user.name,
      salt: state.salted ? EXAMPLE_SALTS[user.username] : null,
      stored,
      found: PRECOMPUTED_TABLE.find((entry) => entry.hash === stored)?.password ?? null,
    };
  });
}
export function saltSummary(state: SaltState): {
  cracked: number;
  equal: boolean;
  message: string;
} {
  const rows = saltRows(state);
  const cracked = rows.filter((row) => row.found !== null);
  const equal = new Set(rows.map((row) => row.stored)).size < rows.length;
  if (!state.looked)
    return {
      cracked: 0,
      equal,
      message: equal
        ? 'Ravi and Jo store the same value, so anyone who reads the table knows their passwords are equal without guessing either one.'
        : "Every row has its own salt, so even Ravi's and Jo's values differ. Equal passwords no longer look equal.",
    };
  return {
    cracked: cracked.length,
    equal,
    message: cracked.length
      ? `${cracked.length} accounts cracked by one lookup: the precomputed table lists ${shortHex(cracked[0]?.stored ?? '')} as ${cracked[0]?.found ?? ''}, so ${listNames(cracked.map((row) => row.name))} both fall.`
      : '0 accounts cracked; each guess must be repeated per account. The table was computed without these salts, so none of its hashes match.',
  };
}

// --- auth-login: what a failed login reveals ------------------------------------------

export type LoginUsername = 'maya' | 'mya';
export type LoginPassword = 'right' | 'wrong';
export interface LoginSettings {
  sameMessage: boolean;
  dummyHash: boolean;
}
export const LOGIN_PASSWORDS: Readonly<Record<LoginPassword, string>> = {
  right: 'blue-kettle-42',
  wrong: 'blue-kettle-24',
};
export interface LoginAnswer {
  username: LoginUsername;
  password: LoginPassword;
  request: string;
  status: 200 | 401;
  body: string;
  detail: string;
  ms: number;
  time: string;
}
/** One scrypt check takes about 100 ms; returning before any hash takes about 1 ms. */
export function loginAnswer(
  username: LoginUsername,
  password: LoginPassword,
  settings: LoginSettings,
): LoginAnswer {
  const request = `POST /auth/login\n\n${JSON.stringify({ username, password: LOGIN_PASSWORDS[password] })}`;
  const known = username === 'maya';
  if (known && password === 'right') {
    const body = JSON.stringify({ id: 1, name: 'Maya', username: 'maya' });
    return {
      username,
      password,
      request,
      status: 200,
      body,
      detail: 'OK',
      ms: 100,
      time: 'about 100 ms',
    };
  }
  const detail = settings.sameMessage
    ? 'Wrong username or password.'
    : known
      ? 'Wrong password.'
      : 'No such user.';
  const ms = known || settings.dummyHash ? 100 : 1;
  return {
    username,
    password,
    request,
    status: 401,
    body: JSON.stringify({ detail }),
    detail,
    ms,
    time: `about ${ms} ms`,
  };
}
export const responseText = (answer: LoginAnswer) =>
  `HTTP/1.1 ${answer.status === 200 ? '200 OK' : '401 Unauthorized'}\ncontent-type: application/json\n\n${answer.body}`;

/** Compare a failure with the other kind of failure under the same server settings. */
export function loginReveals(
  answer: LoginAnswer,
  settings: LoginSettings,
): {
  reveals: boolean;
  message: string;
} {
  if (answer.status === 200)
    return {
      reveals: false,
      message:
        'Logged in as Maya. Only the right username with the right password gets this answer.',
    };
  const unknown = loginAnswer('mya', 'wrong', settings);
  const wrong = loginAnswer('maya', 'wrong', settings);
  const known = answer.username === 'maya';
  if (unknown.detail !== wrong.detail)
    return {
      reveals: true,
      message: known
        ? 'Reveals that maya exists: “Wrong password.” only comes back for a real account.'
        : 'Reveals that mya does not exist: “No such user.” differs from the answer to a wrong password.',
    };
  if (unknown.ms !== wrong.ms)
    return {
      reveals: true,
      message: known
        ? `Reveals that maya exists: the answer took ${wrong.time}, and an unknown name comes back in ${unknown.time}.`
        : `Reveals that mya does not exist: the answer came back in ${unknown.time}, and a real account takes ${wrong.time} to check.`,
    };
  return {
    reveals: false,
    message: 'Reveals nothing: every failure looks the same. Same status, same message, same time.',
  };
}

export interface LoginState extends LoginSettings {
  username: LoginUsername;
  password: LoginPassword;
  /** The answer to the last press of Log in, cleared when a control changes. */
  last: LoginAnswer | null;
  /** The last four answers under the current server settings. */
  log: LoginAnswer[];
}
export const initialLogin = (): LoginState => ({
  username: 'maya',
  password: 'right',
  sameMessage: false,
  dummyHash: false,
  last: null,
  log: [],
});
export type LoginAction =
  | { kind: 'set-username'; username: LoginUsername }
  | { kind: 'set-password'; password: LoginPassword }
  | { kind: 'set-same-message'; on: boolean }
  | { kind: 'set-dummy-hash'; on: boolean }
  | { kind: 'submit' }
  | { kind: 'reset' };
export function loginTransition(state: LoginState, action: LoginAction): LoginState {
  switch (action.kind) {
    case 'reset':
      return initialLogin();
    case 'set-username':
      return { ...state, username: action.username, last: null };
    case 'set-password':
      return { ...state, password: action.password, last: null };
    // Changing the server makes earlier answers incomparable, so they are cleared.
    case 'set-same-message':
      return { ...state, sameMessage: action.on, last: null, log: [] };
    case 'set-dummy-hash':
      return { ...state, dummyHash: action.on, last: null, log: [] };
    case 'submit': {
      const answer = loginAnswer(state.username, state.password, state);
      return { ...state, last: answer, log: [...state.log, answer].slice(-4) };
    }
  }
}
